/** State of the "Poll" part of a post composer (feed, map group, game group,
 * profile): the options being typed and how long the poll runs. The post's
 * own text is the question. Limits mirror the backend's
 * (social/serializers.py SocialPostSerializer). */
export class PostPollDraft {
  static readonly MIN_OPTIONS = 2;
  static readonly MAX_OPTIONS = 6;
  static readonly OPTION_MAX_LENGTH = 80;
  static readonly DURATIONS: { label: string; hours: number | null }[] = [
    { label: '1 day', hours: 24 },
    { label: '3 days', hours: 72 },
    { label: '7 days', hours: 168 },
    { label: 'No end', hours: null },
  ];

  open = false;
  options: string[] = ['', ''];
  hours: number | null = 72;

  /** Non-empty, trimmed options. */
  get filled(): string[] {
    return this.options.map((o) => o.replace(/\s+/g, ' ').trim()).filter(Boolean);
  }

  get hasDuplicates(): boolean {
    const lower = this.filled.map((o) => o.toLowerCase());
    return new Set(lower).size !== lower.length;
  }

  /** Ready to send: 2-6 distinct options. */
  get valid(): boolean {
    const count = this.filled.length;
    return count >= PostPollDraft.MIN_OPTIONS && count <= PostPollDraft.MAX_OPTIONS && !this.hasDuplicates;
  }

  /** What to send with the post, or undefined when no poll is being made. */
  get payload(): { options: string[]; hours: number | null } | undefined {
    return this.open && this.valid ? { options: this.filled, hours: this.hours } : undefined;
  }

  toggle(): void {
    if (this.open) {
      this.reset();
    } else {
      this.open = true;
    }
  }

  addOption(): void {
    if (this.options.length < PostPollDraft.MAX_OPTIONS) {
      this.options = [...this.options, ''];
    }
  }

  removeOption(index: number): void {
    if (this.options.length > PostPollDraft.MIN_OPTIONS) {
      this.options = this.options.filter((_, i) => i !== index);
    }
  }

  setOption(index: number, value: string): void {
    this.options = this.options.map((o, i) => (i === index ? value : o));
  }

  reset(): void {
    this.open = false;
    this.options = ['', ''];
    this.hours = 72;
  }
}
